import { type MouseEvent, type ReactNode } from "react";
import classNames from "classnames";
import { TriangleIcon } from "@icons";
import { styleWithVars } from "@lib/cssVars";
import type { PickerClassNames, PickerTreePosition } from "./Picker.types";
import { PickerItemLabel } from "./PickerItemLabel";
import styles from "./PickerItem.module.scss";
import toggleStyles from "./PickerItemToggles.module.scss";

/**
 * Connector strokes of a tree row: the row's own bar at its depth, keyed on
 * its sibling position, plus one full-height bar per ancestor sibling group
 * that continues below this row, so the stroke never breaks across an
 * expanded parent's children.
 */
export const PickerTreeConnectors = ({
    depth,
    treePosition,
    ancestorLines = [],
}: {
    depth: number;
    treePosition: PickerTreePosition;
    ancestorLines?: readonly number[];
}) => (
    <>
        {ancestorLines.map((lineDepth) => (
            <span
                key={lineDepth}
                aria-hidden
                className={styles.treeConnector}
                data-picker-part="connector"
                data-tree-position="middle"
                style={styleWithVars({
                    "--_picker-connector-depth": lineDepth,
                })}
            />
        ))}
        <span
            aria-hidden
            className={styles.treeConnector}
            data-picker-part="connector"
            data-tree-position={treePosition}
            style={styleWithVars({ "--_picker-connector-depth": depth })}
        />
    </>
);

/** 24px disclosure affordance of a tree row; a Picker sub-part, not a Button size. */
export const PickerExpandToggle = ({
    expanded,
    onExpandedChange,
}: {
    expanded: boolean;
    onExpandedChange?: (expanded: boolean) => void;
}) => (
    <button
        type="button"
        aria-label={expanded ? "Collapse" : "Expand"}
        aria-expanded={expanded}
        tabIndex={-1}
        data-picker-part="expand-toggle"
        className={toggleStyles.expandToggle}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            onExpandedChange?.(!expanded);
        }}
    >
        <TriangleIcon className={toggleStyles.expandToggleIcon} />
    </button>
);

/**
 * The triangle of a flyout row that also selects (multi-select parents): a
 * 16px chromeless button so pressing it opens the level without toggling the
 * row's value. Named after its level and exposing whether it is open.
 */
export const PickerFlyoutOpenToggle = ({
    label,
    isOpen,
    onOpen,
}: {
    label: string | undefined;
    isOpen: boolean;
    onOpen: () => void;
}) => (
    <button
        type="button"
        aria-label={label === undefined ? "Open" : `Open ${label}`}
        aria-expanded={isOpen}
        tabIndex={-1}
        className={toggleStyles.flyoutToggle}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            onOpen();
        }}
    >
        <TriangleIcon className={styles.flyoutIndicator} aria-hidden />
    </button>
);

export interface PickerItemContentProps {
    children: ReactNode;
    leading?: ReactNode;
    /** Secondary text on the label's line; the label keeps at most 60% of it and the hint truncates first. */
    labelHint?: ReactNode;
    description?: ReactNode;
    rightText?: ReactNode;
    trailing?: ReactNode;
    indicator?: ReactNode;
    /** Figma `Triangle=Yes`: an 8px chevron pointing right after the right text. */
    isFlyout?: boolean;
    /** Set when the row itself selects: the triangle becomes the control that opens the level. */
    flyoutOpen?: {
        label: string | undefined;
        isOpen: boolean;
        onOpen: () => void;
    };
    expandable?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    /** A string label cut by its ellipsis shows in full on hover (off when the row has its own tooltip). */
    hasLabelTooltip?: boolean;
}

/**
 * The row anatomy every item-shaped part shares (items, menu toggle rows,
 * submenu triggers): leading slot, label row with the expand toggle, subtitle
 * and the trailing cluster.
 */
export const PickerItemContent = ({
    children,
    leading,
    labelHint,
    description,
    rightText,
    trailing,
    indicator,
    isFlyout = false,
    flyoutOpen,
    expandable = false,
    expanded = false,
    onExpandedChange,
    hasLabelTooltip = false,
}: PickerItemContentProps) => {
    const hasTrailing =
        rightText != null || trailing != null || isFlyout || indicator != null;

    return (
        <>
            {leading !== undefined && (
                <span className={styles.itemLeading}>{leading}</span>
            )}
            <span className={styles.itemBody}>
                <span
                    className={styles.itemLabelRow}
                    data-has-hint={labelHint != null ? "" : undefined}
                >
                    <PickerItemLabel hasTruncationTooltip={hasLabelTooltip}>
                        {children}
                    </PickerItemLabel>
                    {labelHint != null && (
                        <PickerItemLabel
                            hasTruncationTooltip={hasLabelTooltip}
                            className={styles.itemLabelHint}
                            part="label-hint"
                        >
                            {labelHint}
                        </PickerItemLabel>
                    )}
                    {expandable && (
                        <PickerExpandToggle
                            expanded={expanded}
                            onExpandedChange={onExpandedChange}
                        />
                    )}
                </span>
                {description !== undefined && (
                    <span
                        className={styles.itemDescription}
                        data-picker-part="description"
                    >
                        {description}
                    </span>
                )}
            </span>
            {hasTrailing && (
                <span className={styles.itemTrailing}>
                    {rightText != null && (
                        <span
                            className={styles.itemRightText}
                            data-picker-part="right-text"
                        >
                            {rightText}
                        </span>
                    )}
                    {trailing}
                    {indicator}
                    {isFlyout && flyoutOpen === undefined && (
                        <TriangleIcon
                            className={styles.flyoutIndicator}
                            aria-hidden
                        />
                    )}
                    {isFlyout && flyoutOpen !== undefined && (
                        <PickerFlyoutOpenToggle {...flyoutOpen} />
                    )}
                </span>
            )}
        </>
    );
};

/** Class list of an item-shaped row, including the root's `classNames.item` slot. */
export const pickerItemClassName = ({
    hasDescription,
    isDestructive,
    rootClassNames,
    className,
}: {
    hasDescription: boolean;
    isDestructive: boolean;
    rootClassNames: PickerClassNames;
    className?: string;
}) =>
    classNames(
        styles.item,
        {
            [styles.itemWithDescription]: hasDescription,
            [styles.itemDestructive]: isDestructive,
        },
        rootClassNames.item,
        className,
    );
